/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

function PropertiesSearchViewController(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    uiGridConstants: JS5Object,
    CategoriesService: JS5Object,
    DynamicPropertiesService: JS5Object,
    GenresService: JS5Object,
    LanguagesService: JS5Object,
    ModalService: JS5Object,
    NavbarUtilService: JS5Object,
    RolesService: JS5Object,
    SearchFilterService: JS5Object,
    TerritoriesService: JS5Object,
) {

    //=======================//
    // Generic Tab Functions //
    //=======================//

    const setAllDataFunction = (getAllDataFunction: JS5Function, queryParams: JS5Object = undefined) => {
        return getAllDataFunction(queryParams).then((data: JS5Object[]) => {
            this.gridOptions.data = data;
        }).finally(() => {
            this.loadingIndicator.show = false;
        });
    };

    const filterGridBySearchText = (rows: JS5Object) => {
        const searchText = $location.search().searchText;
        if (_.size(searchText)) {
            rows.forEach((row: JS5Object) => {
                _.set(row, 'visible', _.includes(_.toLower(row.entity.name), _.toLower(searchText)));
            });
        }
        return rows;
    };

    const navigateToCreateView = (routeSuffix='') => {
        $location.url(`/system/properties/${this.currentTab.editRoute}/edit/-1${routeSuffix}`);
    };

    const navigateToEditView = (entity: JS5Object) => {
        $location.url(`/system/properties/${this.currentTab.editRoute}/edit/${_.get(entity, 'id')}`);
    };

    //=======//
    // Roles //
    //=======//

    const getCurrentSubTabType = () => {
        return _.get(this, 'currentSubTab.type');
    };

    const setRolesGridData = () => {
        this.currentSubTab = _.get(this.tabs.Roles.subTabs, $location.search().subTab);
        return setAllDataFunction(RolesService.getAllRolesPaginated);
    };

    const processRolesGridFilter = (rows: JS5Object) => {
        const subTabType = getCurrentSubTabType();
        rows = filterGridBySearchText(rows);
        if (!_.isNil(subTabType)) {
            rows.forEach((row: JS5Object) => {
                _.set(row, 'visible', _.get(row, 'visible') && _.isEqual(row.entity.type, subTabType));
            });
        }
        return rows;
    };

    const navigateToRolesCreateView = () => {
        let suffix = '';
        if (!_.isNil(getCurrentSubTabType())) {
            suffix = `/${getCurrentSubTabType()}`;
        }
        navigateToCreateView(suffix);
    };


    //====================//
    // Dynamic Properties //
    //====================//

    const setDynamicPropsGridData = () => {
        return DynamicPropertiesService.queryForProperties()
        .then((propertiesMap: JS5Object) => {
            this.gridOptions.data = _.flatMap(propertiesMap, (value: string, name: string) => {
                if (!DynamicPropertiesService.propIsEditable(name)) {
                    return [];
                }
                return [{
                    name: name,
                    value: value,
                    hasConfidentialInfo: DynamicPropertiesService.propContainsConfidentialInfo(name)
                }];
            });
        });
    };

    const navigateToDynamicPropsCreateView = () => {
        $location.url(`/system/properties/${this.currentTab.editRoute}/edit`);
    };

    const navigateToDynamicPropsEditView = (entity: JS5Object) => {
        let propertyNamePath = encodeURIComponent(_.get(entity, 'name'));
        $location.url(`/system/properties/${this.currentTab.editRoute}/edit`);
        $location.search('propertyName', propertyNamePath);
    };

    const deleteDynamicProperty = () => {
        let entity = _.first(this.gridApi.selection.getSelectedRows());
        this.loadingIndicator.show = true;
        DynamicPropertiesService.deleteProperty(entity.name)
            .then(getData);
    };

    const confirmDynamicPropDeletion = (entity: JS5Object) => {
        ModalService.showMessageModal({
            messageText: `Are you sure you want to delete ${entity.name}?`,
            pullCancelLeft: true,
            title: `Delete ${entity.name}?`,
            windowClass: 'confirm-modal-sm',
            showOK: true
        }, deleteDynamicProperty);
    };


    //=================//
    // Tab Definitions //
    //=================//

    this.tabs = {
        Categories: {
            type: 'Categories',
            displayName: 'Categories',
            editRoute: 'categories',
            setGridDataFunction: () => setAllDataFunction(CategoriesService.getAllCategories),
            processGridFilterFunction: filterGridBySearchText,
            createPropertyFunction: navigateToCreateView,
            editPropertyFunction: navigateToEditView,
            columnDefinitions: [
                {
                    field: 'name',
                    displayName: 'Name',
                    sort: {
                        direction: 'asc',
                    },
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'type.name',
                    displayName: 'Type',
                    cellTemplate: 'components/visual/grid/category-type-cell-template.html'
                },
                {
                    field: 'adPodAcceptProfile.name',
                    displayName: 'AD POD Accept Profile',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'adPodEnabledForUpLynk',
                    displayName: 'AD PROD Enabled for UpLynk',
                    cellTemplate: 'components/visual/grid/radio-button-template.html'
                },
                {
                    field: 'cloneable',
                    displayName: 'Enabled for Clone',
                    cellTemplate: 'components/visual/grid/radio-button-template.html'
                },
                {
                    field: 'aliasCount',
                    displayName: '# of Alias',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                }
            ]
        },
        Genres: {
            type: 'Genres',
            displayName: 'Genres',
            editRoute: 'genres',
            setGridDataFunction: () => setAllDataFunction(GenresService.getAllGenres),
            processGridFilterFunction: filterGridBySearchText,
            createPropertyFunction: navigateToCreateView,
            editPropertyFunction: navigateToEditView,
            columnDefinitions: [
                {
                    field: 'name',
                    displayName: 'Name',
                    sort: {
                        direction: 'asc',
                    },
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'aliasCount',
                    displayName: '# of Alias',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
            ]
        },
        Languages: {
            type: 'Languages',
            displayName: 'Languages',
            editRoute: 'languages',
            setGridDataFunction: () => setAllDataFunction(LanguagesService.getAllLanguagesPaginated),
            processGridFilterFunction: filterGridBySearchText,
            createPropertyFunction: navigateToCreateView,
            editPropertyFunction: navigateToEditView,
            columnDefinitions: [
                {
                    field: 'name',
                    displayName: 'Name',
                    sort: {
                        direction: 'asc',
                    },
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
                },
                {
                    field: 'aITranslate',
                    displayName: 'AI Translate',
                    cellTemplate: 'components/visual/grid/boolean-flag-checkmark-template.html',
                    width: 108,
                },
                {
                    field: 'iso2Code',
                    displayName: 'ISO2 Code',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
                },
                {
                    field: 'locale',
                    displayName: 'Locale',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
                },
                {
                    field: 'aliasCount',
                    displayName: '# of Alias',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
                },
            ],
        },
        Territories: {
            type: 'Territories',
            displayName: 'Territories',
            editRoute: 'territories',
            setGridDataFunction: () => setAllDataFunction(
                TerritoriesService.getAllTerritories,
                {fetchLimit: 10000},
            ),
            processGridFilterFunction: filterGridBySearchText,
            createPropertyFunction: navigateToCreateView,
            editPropertyFunction: navigateToEditView,
            columnDefinitions: [
                {
                    field: 'name',
                    displayName: 'Name',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'value',
                    displayName: 'Value',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'iSO2Code',
                    displayName: 'ISO2 Code',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'iSO3Code',
                    displayName: 'ISO3 Code',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                }
            ],
        },
        Roles: {
            type: 'Roles',
            displayName: 'Roles',
            editRoute: 'roles',
            setGridDataFunction: setRolesGridData,
            processGridFilterFunction: processRolesGridFilter,
            createPropertyFunction: navigateToRolesCreateView,
            editPropertyFunction: navigateToEditView,
            columnDefinitions: [
                {
                    field: 'name',
                    displayName: 'Name',
                    sort: {
                        direction: 'asc',
                    },
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'type',
                    displayName: 'Type',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'aliasCount',
                    displayName: '# of Alias',
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                }
            ],
            subTabs: {
                All: {
                    displayName: 'All'
                },
                Audio: {
                    type: 'Audio',
                    displayName: 'Audio Properties'
                },

                CC: {
                    type: 'CC',
                    displayName: 'Closed Captioning Properties'
                },
                Subtitle: {
                    type: 'Subtitle',
                    displayName: 'Subtitles Properties'
                },
                Video: {
                    type: 'Video',
                    displayName: 'Video Component Properties'
                }
            }
        },
        DynamicProps: {
            type: 'DynamicProps',
            displayName: 'Dynamic Properties',
            editRoute: 'dynamicProperty',
            setGridDataFunction: setDynamicPropsGridData,
            processGridFilterFunction: filterGridBySearchText,
            createPropertyFunction: navigateToDynamicPropsCreateView,
            editPropertyFunction: navigateToDynamicPropsEditView,
            deletePropertyFunction: confirmDynamicPropDeletion,
            columnDefinitions: [
                {
                    field: 'name',
                    displayName: 'Name',
                    sort: {
                        direction: 'asc',
                    },
                    cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
                },
                {
                    field: 'value',
                    displayName: 'Value',
                    cellTemplate: 'system/properties/searchView/cellTemplates/dynamic-prop-value-template.html'
                }
            ]
        }
    };


    //======//
    // Grid //
    //======//

    this.processGridFilter = (rows: JS5Object) => {
        return _.invoke(this.currentTab, 'processGridFilterFunction', rows);
    };

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: false,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableSorting: true,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        enableHorizontalScrollbar: uiGridConstants.ALWAYS,
        enableVerticalScrollbar: uiGridConstants.ALWAYS,
        rowHeight: 26,
        columnDefs: [],
        data: [],
        multiSelect: false,
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
            this.gridApi.grid.registerRowsProcessor(this.processGridFilter, 200);
        },
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html'
    };

    this.onRowDblClick = (row: JS5Object) => {
        _.invoke(this.currentTab, 'editPropertyFunction', row.entity);
    };


    //================//
    // Template Logic //
    //================//

    this.changeTab = (tab: JS5Object) => {
        if (_.isEqual(tab, this.tabs.Roles)) {
            $location.search('tab', tab.type);
        } else {
            $location.search('tab', tab.type);
            $location.search('subTab', null);
        }
        getData();
    };
    this.changeSubTab = (subTab: JS5Object) => {
        this.currentSubTab = subTab;
        $location.search('subTab', subTab.type);
        getData();
    };

    const onCreateButtonClicked = () => {
        _.invoke(this.currentTab, 'createPropertyFunction');
    };

    const onEditButtonClicked = () => {
        _.invoke(this.currentTab, 'editPropertyFunction', _.first(this.gridApi.selection.getSelectedRows()));
    };

    const onDeleteButtonClicked = () => {
        _.invoke(this.currentTab, 'deletePropertyFunction', _.first(this.gridApi.selection.getSelectedRows()));
    };

    this.toolbarButtonDefs = [
        {
            label: 'New',
            enabled: () => true,
            onClick: onCreateButtonClicked
        },
        {
            label: 'Edit',
            enabled: () => _.size(this.gridApi.selection.getSelectedRows()),
            onClick: onEditButtonClicked
        },
        {
            label: 'Delete',
            enabled: () => _.get(this.currentTab, 'deletePropertyFunction') && _.size(this.gridApi.selection.getSelectedRows()),
            onClick: onDeleteButtonClicked
        }
    ];


    //=================//
    // Lifecycle Hooks //
    //=================//

    const getData = () => {
        this.currentTab = _.get(this.tabs, $location.search().tab, this.tabs.Categories);
        this.gridOptions.columnDefs = _.get(this.currentTab, 'columnDefinitions');
        this.loadingIndicator.show = true;
        _.invoke(this.currentTab, 'setGridDataFunction').finally(() => {
            this.loadingIndicator.show = false;
        });
    };

    this.$onInit = () => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };
        NavbarUtilService.viewSearchFunction = getData;
        SearchFilterService.populateCriteriaFromLocationSearch();
        getData();
    };
}

angular.module('prism-ui.properties').component('propertiesSearchView', {
    templateUrl: 'system/properties/searchView/properties-search-view-template.html',
    controller: PropertiesSearchViewController
});
